import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { NotificationsLevel } from "../../../lib/enums";
import { show } from "../../../notifications/notifications";
import useRlCloudSandboxReport from "../../samples_cloud/hooks/useRlCloudSandboxReport";
import api from "../api";
import {
  LinkSearchRules,
  LLMSummaryStatus
} from "../components/report_summary/cards/llm_summary/@types/LLMSummary";
import { findPivotLinks } from "../components/report_summary/cards/llm_summary/components/utils";
import {
  LINK_SEARCH_LIST,
  LINK_SEARCH_LIST_EXTRACTED_FILES
} from "../components/report_summary/cards/llm_summary/consts";
import { sampleReportKeys } from "../queryKeys";
import useLocalSampleSummaryParams from "./useLocalSampleSummaryParams";

const FETCH_INTERVAL = 5000;

export const useGenerateLLMSummary = () => {
  const { hash, sourceId, relationshipId } = useLocalSampleSummaryParams();

  const {
    mutateAsync: generateLLMSummary,
    isError,
    error,
    isPending
  } = useMutation({
    mutationFn: () =>
      api.generateLLMSummary(hash ?? "", sourceId ?? 0, relationshipId)
  });

  return { generateLLMSummary, isError, error, isPending };
};

export const useCheckLLMSummaryHasChanged = (enabled = true) => {
  const { hash, sourceId, relationshipId } = useLocalSampleSummaryParams();

  const { data, isLoading } = useQuery({
    queryKey: sampleReportKeys.getLLMSummaryHasChanged(
      hash,
      sourceId,
      relationshipId
    ),
    queryFn: () =>
      api.getLLMSummaryHasChanged(hash ?? "", sourceId ?? 0, relationshipId),
    enabled
  });

  return {
    hasChanged: data?.data?.has_changed ?? false,
    isLoading
  };
};

const useSearchLinks = (sha1 = ""): LinkSearchRules[] => {
  const { isSuccess } = useRlCloudSandboxReport(sha1);
  return isSuccess ? LINK_SEARCH_LIST : LINK_SEARCH_LIST_EXTRACTED_FILES;
};

export const useLLMSummary = (pregenerate = false) => {
  const { hash, sourceId, relationshipId } = useLocalSampleSummaryParams();
  const queryClient = useQueryClient();
  const [isRequesting, setIsRequesting] = useState(false);
  const searchLinks = useSearchLinks(hash);

  const {
    generateLLMSummary: genSummary,
    isPending: genSummaryIsPending,
    isError: genSummaryIsError,
    error: genSummaryError
  } = useGenerateLLMSummary();

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: sampleReportKeys.getLLMSummary(
      hash,
      sourceId,
      relationshipId,
      pregenerate
    ),
    queryFn: () =>
      api.getLLMSummary(hash ?? "", sourceId ?? 0, relationshipId, pregenerate),
    enabled: !!hash && !!sourceId,
    refetchInterval: responseData =>
      [LLMSummaryStatus.PROCESSING, LLMSummaryStatus.QUEUED].includes(
        responseData?.state.data?.data?.status ?? LLMSummaryStatus.NOT_FOUND
      )
        ? FETCH_INTERVAL
        : false,
    select: ({ data: responseData }) => {
      if (!responseData.summary?.summary) {
        return responseData;
      }
      return {
        ...responseData,
        summary: {
          ...responseData.summary,
          summary: Object.fromEntries(
            Object.entries(responseData.summary.summary).map(([key, entry]) => [
              key,
              entry
                ? { ...entry, links: findPivotLinks(entry, searchLinks) }
                : entry
            ])
          )
        }
      };
    }
  });

  useEffect(() => {
    if (genSummaryIsPending || (isLoading && pregenerate)) {
      setIsRequesting(true);
    } else if (
      data?.status != null &&
      data?.status !== LLMSummaryStatus.NOT_FOUND
    ) {
      setIsRequesting(false);
    }
  }, [genSummaryIsPending, data, isLoading, pregenerate]);

  const { hasChanged, isLoading: changesIsLoading } =
    useCheckLLMSummaryHasChanged(
      data?.status === LLMSummaryStatus.DONE &&
        !genSummaryIsPending &&
        !isLoading
    );

  const generateLLMSummary = () => {
    genSummary()
      .then(response => {
        if (response.status === 200) {
          refetch();
        }
      })
      .catch(() => {
        show({
          level: NotificationsLevel.ERROR,
          message: "Error while generating summary. Please try again."
        });
      });
  };

  const refetchLLMSummary = () => {
    queryClient.removeQueries({
      queryKey: sampleReportKeys.getLLMSummary(
        hash,
        sourceId,
        relationshipId,
        pregenerate
      )
    });
    queryClient.removeQueries({
      queryKey: sampleReportKeys.getLLMSummaryHasChanged(
        hash,
        sourceId,
        relationshipId
      )
    });

    generateLLMSummary();
  };

  return {
    hasChanged,
    status: data?.status ?? LLMSummaryStatus.NOT_FOUND,
    summary: !genSummaryIsPending ? data?.summary : undefined,
    summaryIsLoading:
      data?.status === LLMSummaryStatus.QUEUED ||
      data?.status === LLMSummaryStatus.PROCESSING ||
      isLoading ||
      isFetching ||
      genSummaryIsPending ||
      isRequesting,
    summaryIsProcessing:
      data?.status === LLMSummaryStatus.QUEUED ||
      data?.status === LLMSummaryStatus.PROCESSING ||
      isRequesting,
    changesIsLoading,
    isError: genSummaryIsError || isError,
    error: genSummaryIsError ? genSummaryError : error,
    generateLLMSummary,
    refetchLLMSummary
  };
};
